import React, { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { checkPatientForms } from "../../../../store/slices/casesSlice";
import { selectHasPermission } from "../../../../store/slices/authSlice";
import { programConfigs } from "../../../../config/programConfigs";
import MainLayout from "../../../../components/layout/MainLayout";
import Breadcrumbs from "../../../../components/common/Breadcrumbs";
import { formDeleteThunks } from "../../../../config/formDeleteConfigs";
import Swal from "sweetalert2";

const CaseProfile = () => {
    const { state } = useLocation();
    const { caseId, patientId: statePatientId } = state || {};
    const dispatch = useDispatch();
    const { patientForms, currentCase, isLoading } = useSelector((state) => state.cases);
    const navigate = useNavigate();
    const programData = currentCase?.program || null;

    // Map program names to config keys
    const programMap = {
        "Day Rehabilitation Program": "DAY_REHAB_PROGRAM",
        "Home Visitation Program": "HOME_VISITATION_PROGRAM",
        "Residential Rehab Program": "RESIDENTIAL_REHAB_PROGRAM",
    };

    const activeProgram = programConfigs[programMap[programData?.name]] || null;
    const phases = activeProgram?.phases || [];
    const formRoutes = activeProgram?.formRoutes || {};

    // Forms that have print routes (based on your App.js)
    // Note: Make sure these paths match EXACTLY with your App.js routes
    const printableFormRoutes = {
        "Inquiry Form": "/inquiry/print",
        "Consent Form": "/consents/print",
        "Escort Form [injection]": "/injections/print",
        "Personal Belonging": "/personal-belongings/print",
        "Tentative Family Plan": "/phase04/tentative-family-plan/print",
        "Pros Form": "/phase05/pros-assessments/print",
        "ADL Record Sheet": "/phase05/adl-record-sheet/print",
        "Family Meeting Minutes": "/phase05/family-meeting-minutes/print",
        "Incident Report": "/phase05/incident-report/print",
        "IRP (Individual Rehabilitation Plan)": "/phase05/individualised-rehab-plan/print",
        "Progress Notes Record (Phsyician)[Sana]": "/phase05/sana-notes/print",
        "Nursing Record": "/phase05/nursing-records/print",
        "Progress Notes Record (Psychiatric) - RPR": "/phase05/progress-notes/print",
        "Progress Notes Record (PRPS) - RPR": "/phase05/progress-notes/print",
        "Progress Notes Record (RPR)": "/phase05/progress-notes/print",
        "Depot injection Chart": "/phase05/depot-injections/print",
        "Vitals Monitoring Chart": "/phase05/vitals-monitor/print",
        "Discharge Advice": "/phase07/discharge-advice/print",
        "Lama Policy": "/optional/lama-form/print",
        "Referral Form": "/optional/referral-form/print",
        "Relocation Policy": "/optional/relocation-form/print",
        "Safety Plan": "/optional/safety-plan/print",
        "Suicide Detail": "/optional/suicide-detail/print",
        "Violence Detail": "/optional/violence-risk/print",
        "Psych Order": "/phase03/psychiatrist-order/print",
        "Exercise Schedule": "/exercise-schedule/print",
        "Medication Sheet": "/phase05/medication-sheets/print",
        "Anxiety Form": "/phase04/anxiety/print",
        "BDI-II Form": "/phase04/bdi/print",
        "BPRS Form": "/phase04/bprs/print",
        "Complaint Form": "/phase03/complaint-form/print",
        "Beck Suicide Intent Scale": "/phase04/suicide-intent/print",
        "Depression Patient Health Question": "/phase04/phq/print",
        "Obsessive Compulsive Disorder Test": "/phase04/obsessive-compulsive-test/print",
        "Qol2": "/phase04/qol/print",
        "YMRS Form": "/phase04/ymrs/print",
        "Safety Plan (URDU)": "/optional/safety-plan-urdu/print",
        "Integrated Assessment Form": "/residential/phase-01/integrated-initial-assessment/print",
        // All Discharge Summary types use the same print route
        "Discharge Summary (Psychiatric)": "/phase07/discharge-summary/print",
        "Discharge Summary (Physician)": "/phase07/discharge-summary/print",
        "Discharge Summary (Nursing)": "/phase07/discharge-summary/print",
        "Discharge Summary (PRPS)": "/phase07/discharge-summary/print",
        "Discharge Summary": "/phase07/discharge-summary/print", // Generic version for programs without types
    };

    const [summaryData, setSummaryData] = useState(null);
    const patientId = statePatientId;

    // Fetch patient forms data when component mounts
    useEffect(() => {
        if (patientId && caseId) {
            dispatch(checkPatientForms({ patientId, caseId }))
                .unwrap()
                .then((res) => {
                    if (res.summary) {
                        setSummaryData(res.summary);
                    }
                })
                .catch((error) => {
                    Swal.fire("Error", error || "Failed to load patient forms", "error");
                });
        }
    }, [dispatch, patientId, caseId]);

    // Component to handle individual form card with permission checks
    const FormCard = ({ formName, phaseName }) => {
        // Get form data from the correct location in state
        const formData = patientForms?.[phaseName]?.[formName] || { filled: false };
        const permissions = formData?.permissions || {};

        // Check permissions using Redux selector
        const canManage = useSelector((state) =>
            selectHasPermission(state, permissions.manage)
        );
        const canCreate = useSelector((state) =>
            selectHasPermission(state, permissions.create)
        );
        const canEdit = useSelector((state) =>
            selectHasPermission(state, permissions.edit)
        );
        const canDelete = useSelector((state) =>
            selectHasPermission(state, permissions.delete)
        );

        // If user doesn't have manage permission, don't render the card
        if (!canManage) {
            return null;
        }

        // Extract type label for display (e.g., "Psychiatric", "Physician")
        const getTypeLabel = (name) => {
            const match = name.match(/\((.*?)\)/);
            return match ? match[1] : null;
        };

        const typeLabel = getTypeLabel(formName);

        // Check if this form has a print route available
        const hasPrintRoute = printableFormRoutes.hasOwnProperty(formName);

        return (
            <div className="col-md-6 col-lg-4 d-flex">
                <div
                    className={`border rounded-4 p-3 flex-fill shadow-sm bg-white d-flex flex-column justify-content-between ${formData.filled ? "border-primary-subtle" : "border-secondary-subtle"
                        }`}
                    style={{ minHeight: "150px", transition: "all 0.2s ease-in-out" }}
                >
                    <div className="d-flex justify-content-between align-items-start mb-2">
                        <div className="flex-grow-1">
                            <h6 className="mb-0 fw-semibold text-dark">{formName}</h6>
                            {typeLabel && (
                                <span className="badge bg-info-subtle text-info mt-1">
                                    <i className="ri-user-line me-1"></i>{typeLabel}
                                </span>
                            )}
                        </div>
                        {formData.filled ? (
                            <span className="badge bg-success-subtle text-success d-flex align-items-center">
                                <i className="ri-check-line me-1"></i> Completed
                            </span>
                        ) : (
                            <span className="badge bg-warning-subtle text-warning d-flex align-items-center">
                                <i className="ri-time-line me-1"></i> Pending
                            </span>
                        )}
                    </div>

                    {formData.filled ? (
                        <>
                            <div className="d-flex justify-content-between align-items-center small text-muted mt-auto">
                                <div className="text-dark bg-primary-subtle rounded-4 fw-bold p-1">
                                    <i className="ri-calendar-line me-1 text-dark"></i>
                                    <small className="fw-semibold text-dark">
                                        {formData.created_at ? formData.created_at.split("T")[0] : "N/A"}
                                    </small>
                                    <small className="text-secondary ms-1">
                                        • {formData.created_by_name || "Unknown"}
                                    </small>
                                </div>
                                <div className="text-dark bg-secondary-subtle rounded-4 fw-bold p-1">
                                    <i className="ri-refresh-line me-1 text-secondary"></i>
                                    <small className="fw-semibold text-dark">
                                        {formData.updated_at ? formData.updated_at.split("T")[0] : "N/A"}
                                    </small>
                                </div>
                            </div>

                            <div className="d-flex gap-2 mt-3">
                                {canEdit && hasPrintRoute && (
                                    <button
                                        className="btn btn-outline-secondary btn-sm flex-fill"
                                        onClick={() => handleFormAction(formName, "print", formData.id, formData.type_id)}
                                    >
                                        <i className="ri-printer-line me-1"></i> Print
                                    </button>
                                )}
                                {canEdit && (
                                    <button
                                        className="btn btn-outline-primary btn-sm flex-fill"
                                        onClick={() => handleFormAction(formName, "edit", formData.id, formData.type_id)}
                                    >
                                        <i className="ri-edit-line me-1"></i> Edit
                                    </button>
                                )}
                                {canDelete && formName !== "Inquiry Form" && ( // Added formName check here
                                    <button
                                        className="btn btn-outline-danger btn-sm flex-fill"
                                        onClick={() => handleFormAction(formName, "delete", formData.id, formData.type_id)}
                                    >
                                        <i className="ri-delete-bin-line me-1"></i> Delete
                                    </button>
                                )}
                                {!canEdit && !canDelete && (
                                    <p className="text-muted small mb-0 text-center w-100">
                                        No actions available
                                    </p>
                                )}
                            </div>
                        </>
                    ) : (
                        <div className="mt-auto">
                            {canCreate ? (
                                <button
                                    className="btn btn-sm btn-primary w-100 mt-3"
                                    onClick={() => handleFormAction(formName, "add", null, formData.type_id)}
                                >
                                    <i className="ri-add-line me-1"></i> Add {formName}
                                </button>
                            ) : (
                                <p className="text-muted small mb-0 text-center mt-3">
                                    No permission to create
                                </p>
                            )}
                        </div>
                    )}
                </div>
            </div>
        );
    };

    // Navigation logic for add/edit/delete/print
    const handleFormAction = (formName, action, recordId = null, typeId = null) => {
        const navState = {
            caseId,
            patientId,
            formName,
            // Pass type_id if exists
            ...(typeId && { type_id: typeId }),
            // Pass program data for discharge summary
            ...(formName.includes("Discharge Summary") && programData ? { program: programData } : {}),
        };

        if (action === "print") {
            // Use the printableFormRoutes mapping
            const printPath = printableFormRoutes[formName];

            if (!printPath) {
                Swal.fire({
                    icon: "info",
                    title: "Print Not Available",
                    text: `Print functionality is not yet implemented for "${formName}"`,
                });
                return;
            }

            if (!recordId) {
                Swal.fire({
                    icon: "error",
                    title: "No Record Found",
                    text: `Cannot print "${formName}" - no record ID available`,
                });
                return;
            }

            // Navigate to print route
            navigate(`${printPath}/${recordId}`, { state: navState });
            return;
        }

        // For add/edit/delete, use the formRoutes mapping
        const basePath = formRoutes[formName];
        if (!basePath) {
            Swal.fire({
                icon: "error",
                title: "Route Not Found",
                text: `Navigation route not configured for "${formName}"`,
            });
            return;
        }

        if (action === "add") {
            navigate(`${basePath}/add`, { state: navState });
        } else if (action === "edit") {
            if (!recordId) {
                Swal.fire({
                    icon: "error",
                    title: "No Record Found",
                    text: `Cannot edit "${formName}" - no record ID available`,
                });
                return;
            }
            navigate(`${basePath}/edit/${recordId}`, { state: navState });
        } else if (action === "delete") {
            if (!recordId) {
                Swal.fire({
                    icon: "error",
                    title: "No Record Found",
                    text: `Cannot delete "${formName}" - no record ID available`,
                });
                return;
            }

            Swal.fire({
                title: "Are you sure?",
                text: `"${formName}" will be permanently deleted. This action cannot be undone.`,
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#d33",
                cancelButtonColor: "#3085d6",
                confirmButtonText: "Yes, delete it!",
                cancelButtonText: "Cancel",
            }).then(async (result) => {
                if (result.isConfirmed) {
                    try {
                        // 1. Get the thunk from our new config file
                        const deleteThunk = formDeleteThunks[formName];

                        if (!deleteThunk) {
                            throw new Error(`Delete logic not configured for "${formName}"`);
                        }

                        // 2. Dispatch and wait for completion
                        await dispatch(deleteThunk(recordId)).unwrap();

                        // 3. Success Feedback
                        Swal.fire({
                            icon: "success",
                            title: "Deleted!",
                            text: `"${formName}" has been removed successfully.`,
                            timer: 2000,
                            showConfirmButton: false
                        });

                        // 4. Refresh the patient forms status to update the UI cards
                        dispatch(checkPatientForms({ patientId, caseId }));

                    } catch (error) {
                        Swal.fire({
                            icon: "error",
                            title: "Deletion Failed",
                            text: error.message || "An error occurred while deleting.",
                        });
                    }
                }
            });
        }
    };

    // Calculate current phase number based on case status
    let currentPhaseNumber = 1;
    const statusName = currentCase?.status?.name?.toLowerCase() || "";

    if (statusName === "completed") {
        currentPhaseNumber = 7;
    } else {
        const num = parseInt(statusName.replace(/\D/g, ""));
        currentPhaseNumber = isNaN(num) ? 1 : num;
    }

    const visiblePhases = phases.filter((phase) => phase.id <= currentPhaseNumber);

    if (isLoading) {
        return (
            <MainLayout>
                <div className="container-xxl flex-grow-1 container-p-y">
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status" style={{ width: "3rem", height: "3rem" }}>
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3 text-muted">Loading case information...</p>
                    </div>
                </div>
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <Breadcrumbs />

                <div className="card shadow-sm border-0">
                    <div className="card-header text-center bg-white border-0 pb-0">
                        <p className="text-dark bg-primary-subtle rounded-4 fw-bold d-block my-3">
                            Case Information
                        </p>
                    </div>

                    <div className="card-body p-4">
                        {/* Case Summary Fields */}
                        <div className="row g-2 mb-4">
                            {[
                                { label: "Patient Name", value: summaryData?.patient_name || "N/A" },
                                { label: "Case Number", value: summaryData?.case_number || "N/A" },
                                { label: "Current Program", value: currentCase?.program?.name || "N/A" },
                                {
                                    label: "Completed Forms",
                                    value: summaryData?.completed_forms || "0/0",
                                },
                            ].map((field, i) => (
                                <div key={i} className="col-md-3">
                                    <div className="border rounded-3 p-2 bg-light-subtle h-100">
                                        <label className="form-label fw-semibold text-secondary mb-1" style={{ fontSize: '0.875rem' }}>
                                            {field.label}
                                        </label>
                                        <input
                                            type="text"
                                            className="form-control form-control-sm"
                                            value={field.value}
                                            readOnly
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Phases Accordion */}
                        <div className="accordion" id="casePhasesAccordion">
                            {visiblePhases.map((phase, index) => {
                                // Count completed forms for this phase
                                const phaseFormsData = patientForms?.[phase.name] || {};
                                const completedCount = Object.values(phaseFormsData).filter(
                                    (f) => f.filled
                                ).length;

                                return (
                                    <div
                                        key={phase.id}
                                        className="accordion-item border-0 mb-3 rounded-3 shadow-sm"
                                    >
                                        <h2 className="accordion-header">
                                            <button
                                                className={`accordion-button ${index !== 0 ? "collapsed" : ""
                                                    } fw-semibold`}
                                                type="button"
                                                data-bs-toggle="collapse"
                                                data-bs-target={`#phase-${phase.id}`}
                                            >
                                                <i className="ri-folder-open-line me-2 text-primary"></i>
                                                Phase {phase.id}: {phase.name}
                                                <span className="badge bg-primary-subtle text-dark ms-3">
                                                    {completedCount}/{phase.forms.length} Completed
                                                </span>
                                            </button>
                                        </h2>

                                        <div
                                            id={`phase-${phase.id}`}
                                            className={`accordion-collapse collapse ${index === 0 ? "show" : ""
                                                }`}
                                            data-bs-parent="#casePhasesAccordion"
                                        >
                                            <div className="accordion-body bg-primary-subtle rounded-bottom-3 py-4">
                                                {phase.forms.length === 0 ? (
                                                    <p className="text-muted text-center mb-0">
                                                        No forms configured for this phase.
                                                    </p>
                                                ) : (
                                                    <div className="row g-3">
                                                        {phase.forms.map((formName) => (
                                                            <FormCard
                                                                key={formName}
                                                                formName={formName}
                                                                phaseName={phase.name}
                                                            />
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default CaseProfile;